import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getSubscriptionUsage, 
  canPerformAnalysis,
  incrementUsage,
  trackUsage
} from '../services/usageService';

export interface UsageState {
  isLoading: boolean;
  error: string | null;
  subscription: any | null;
  usedCount: number;
  limit: number;
  remaining: number;
  percentage: number;
  canPerformAnalysis: boolean;
  hasActiveSubscription: boolean;
  planType: string;
}

// Utility function to debounce function calls
const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function(...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
};

/**
 * React hook for tracking subscription usage in components
 */
export const useSubscriptionUsage = () => {
  const [state, setState] = useState<UsageState>({
    isLoading: true,
    error: null,
    subscription: null,
    usedCount: 0,
    limit: 0,
    remaining: 0,
    percentage: 0,
    canPerformAnalysis: false,
    hasActiveSubscription: false,
    planType: 'free'
  });
  
  // Use a ref to track the last fetch time to prevent too frequent refreshes
  const lastFetchTimeRef = useRef<number>(0);
  // Use a ref to track if the component is mounted
  const isMountedRef = useRef<boolean>(true);
  // Track if we're currently processing an event
  const isProcessingEventRef = useRef<boolean>(false);

  // Fetch the current subscription and usage data
  const fetchUsage = useCallback(async (force = false) => {
    // Skip if already fetching or if the fetch was triggered too recently (unless force=true)
    const now = Date.now();
    if (!force && (state.isLoading || now - lastFetchTimeRef.current < 5000)) {
      return;
    }
    
    // Update last fetch time
    lastFetchTimeRef.current = now;
    
    // Only update loading state if we're not already loading
    if (!state.isLoading) {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
    }
    
    try {
      const data = await getSubscriptionUsage();
      
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      
      if (!data || !data.subscription) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to retrieve subscription information',
          hasActiveSubscription: false,
          canPerformAnalysis: false
        }));
        return;
      }
      
      const subscription = data.subscription;
      const isActive = subscription.is_active && 
        (subscription.status === 'active' || 
         subscription.status === 'trialing' || 
         subscription.status === 'free');
      
      // Default limits based on plan type
      const defaultLimit = 
        subscription.plan_type === 'premium' ? 1500 :
        subscription.plan_type === 'standard' ? 500 : 10;
      
      const limit = subscription.analyses_limit || defaultLimit;
      const usedCount = subscription.analyses_used || 0;
      const remaining = Math.max(0, limit - usedCount);
      const percentage = limit > 0 ? Math.min(100, (usedCount / limit) * 100) : 100;
      
      setState({
        isLoading: false,
        error: null,
        subscription,
        usedCount,
        limit,
        remaining,
        percentage,
        canPerformAnalysis: isActive && remaining > 0,
        hasActiveSubscription: isActive,
        planType: subscription.plan_type || 'free'
      });
    } catch (error) {
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      
      console.error('Error in useSubscriptionUsage hook:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
    }
  }, [state.isLoading]);

  // Debounced event handler
  const handleUsageUpdateDebounced = useCallback(
    debounce(() => {
      // Skip if already processing an event
      if (isProcessingEventRef.current) return;
      
      isProcessingEventRef.current = true;
      
      console.log('Usage update event received, refreshing subscription data');
      fetchUsage(true).finally(() => {
        isProcessingEventRef.current = false;
      });
    }, 300),
    [fetchUsage]
  );

  // Load data when the hook mounts
  useEffect(() => {
    isMountedRef.current = true;
    fetchUsage();
    
    // Create event listener for usage updates
    const handleUsageUpdate = () => handleUsageUpdateDebounced();
    
    // Listen for the custom 'usage-updated' event
    window.addEventListener('usage-updated', handleUsageUpdate);
    
    // Cleanup listener when component unmounts
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('usage-updated', handleUsageUpdate);
    };
  }, [fetchUsage, handleUsageUpdateDebounced]);

  // Function to increment usage and update state
  const incrementAndUpdate = useCallback(async (count: number = 1, analysisType?: string) => {
    // If we know we can't perform, don't even try
    if (!state.canPerformAnalysis) {
      return {
        success: false,
        error: 'Usage limit exceeded or no active subscription',
        remaining: state.remaining
      };
    }
    
    try {
      let result;
      
      // If analysis type is provided, use the tracking function
      if (analysisType) {
        const success = await trackUsage(analysisType);
        result = {
          success,
          error: success ? null : 'Failed to track usage',
          remaining: Math.max(0, state.remaining - (success ? count : 0))
        };
      } else {
        // Otherwise just increment
        result = await incrementUsage(count);
      }
      
      // Refresh data after increment
      await fetchUsage(true);
      
      return result;
    } catch (error) {
      console.error('Error incrementing usage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        remaining: state.remaining
      };
    }
  }, [fetchUsage, state.canPerformAnalysis, state.remaining]);

  // Check if an analysis can be performed
  const checkAnalysisPermission = useCallback(async () => {
    try {
      const result = await canPerformAnalysis();
      
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return result;
      
      // Only update the canPerform state
      setState(prev => ({
        ...prev,
        canPerformAnalysis: result.canPerform,
        remaining: result.remainingUses || 0
      }));
      
      return result;
    } catch (error) {
      console.error('Error checking analysis permission:', error);
      return {
        canPerform: false,
        message: 'Error checking permission'
      };
    }
  }, []);

  return {
    ...state,
    fetchUsage: useCallback(() => fetchUsage(true), [fetchUsage]),
    incrementUsage: incrementAndUpdate,
    checkAnalysisPermission,
    
    // Helper computed properties
    isPremium: state.planType === 'premium',
    isStandard: state.planType === 'standard',
    isFree: state.planType === 'free',
    isUnlimited: state.limit === 0 || state.limit === Infinity,
    
    // Usage indicators
    usageLevel: 
      state.percentage >= 90 ? 'critical' :
      state.percentage >= 75 ? 'warning' :
      state.percentage >= 50 ? 'moderate' : 'low'
  };
}; 